import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { PersonAdd, Search } from '@mui/icons-material';
import Swal from 'sweetalert2';
import paymentportalservice from '../../../services/paymentportalservice';
import { initials } from './campaignFieldUtils';

// Who — beyond super admins and the campaign's own creator — can see this
// campaign. Both of those are left out of the list by the API, since
// unticking them couldn't actually take their access away.
const ShareCampaignDialog = React.memo(function ShareCampaignDialog({ open, campaign, onClose, onSaved }) {
    const [users, setUsers] = useState([]);
    // Super admins — always visible regardless of sharing (common/useBaseCondition.js on the
    // backend) — surfaced by name rather than just the rule below, since "who" is the actual
    // question when opening this dialog.
    const [alwaysVisible, setAlwaysVisible] = useState([]);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    // What the server currently has, kept apart from selectedIds (which
    // tracks unsaved edits) so rows can be ordered and badged by the saved
    // state rather than jumping about as boxes are ticked.
    const [initialSharedIds, setInitialSharedIds] = useState(() => new Set());
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState('');

    const campaignId = campaign?._id || campaign?.id;

    // Re-fetched on every open rather than cached, so the ticks always match
    // what is actually stored — access can also change from another tab, or
    // as a side effect of the campaign being shared elsewhere.
    useEffect(() => {
        if (!open || !campaignId) return;

        let cancelled = false;
        setLoading(true);
        setLoadError('');
        setSearch('');

        paymentportalservice
            .FetchCampaignShares(campaignId)
            .then((data) => {
                if (cancelled) return;
                const fetched = Array.isArray(data?.users) ? data.users : [];
                const sharedIds = fetched.filter((u) => u.shared).map((u) => String(u._id));
                setUsers(fetched);
                setAlwaysVisible(Array.isArray(data?.alwaysVisible) ? data.alwaysVisible : []);
                // Two separate Sets, not one shared reference — mutating the
                // selection must not rewrite the saved-state baseline.
                setSelectedIds(new Set(sharedIds));
                setInitialSharedIds(new Set(sharedIds));
            })
            .catch((error) => {
                if (cancelled) return;
                setUsers([]);
                setAlwaysVisible([]);
                setSelectedIds(new Set());
                setInitialSharedIds(new Set());
                setLoadError(error?.response?.data || error.message || 'Could not load users');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, campaignId]);

    // Whoever already has access floats to the top, so "who can see this"
    // is the first thing in view instead of something to hunt for down an
    // alphabetical list of everyone.
    const orderedUsers = useMemo(() => {
        const shared = [];
        const rest = [];
        users.forEach((user) => {
            (initialSharedIds.has(String(user._id)) ? shared : rest).push(user);
        });
        return shared.concat(rest);
    }, [users, initialSharedIds]);

    const filteredUsers = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return orderedUsers;
        return orderedUsers.filter(
            (user) =>
                (user.fullName || '').toLowerCase().includes(term) ||
                (user.email || '').toLowerCase().includes(term) ||
                (user.role || '').toLowerCase().includes(term)
        );
    }, [orderedUsers, search]);

    // Drawn from the full roster, not the filtered rows, so narrowing the
    // search never makes someone look un-shared.
    const selectedUsers = useMemo(
        () => users.filter((user) => selectedIds.has(String(user._id))),
        [users, selectedIds]
    );

    // "and you" always applies — only a super admin can reach this dialog (the Share icon
    // it opens from is itself super-admin-only), and the backend leaves the requester out of
    // alwaysVisible for exactly this reason.
    const alwaysVisibleText = useMemo(() => {
        const names = alwaysVisible.map((user) => user.fullName || 'Unnamed user');
        return [...names, 'you'].join(', ');
    }, [alwaysVisible]);

    const toggleUser = useCallback((userId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    }, []);

    // Acts on the filtered rows only, so it stays predictable while a search
    // is narrowing the list — selecting all of 4 visible people should not
    // quietly grant access to 200 others.
    const allFilteredSelected =
        filteredUsers.length > 0 && filteredUsers.every((user) => selectedIds.has(String(user._id)));

    const toggleAllFiltered = useCallback(() => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const ids = filteredUsers.map((user) => String(user._id));
            const shouldSelect = !ids.every((id) => next.has(id));
            ids.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
            return next;
        });
    }, [filteredUsers]);

    const handleSave = async () => {
        if (!campaignId) return;
        setSaving(true);
        try {
            const userIds = Array.from(selectedIds);
            await paymentportalservice.UpdateCampaignShares(campaignId, userIds);
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: userIds.length
                    ? `Shared with ${userIds.length} user${userIds.length === 1 ? '' : 's'}`
                    : 'Campaign is no longer shared',
                showConfirmButton: false,
                timer: 1400,
            });
            onSaved?.(campaignId, userIds);
            onClose?.();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Could not update access',
                text: error?.response?.data || error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <PersonAdd sx={{ color: '#0891B2' }} />
                    <Typography variant="h6">Share Campaign</Typography>
                </Stack>
                {campaign?.name && (
                    <Typography variant="body2" color="text.secondary">
                        {campaign.name}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">
                        Already visible to {loading ? 'loading…' : alwaysVisibleText} — not
                        listed below since that can't be changed. Selected users see it too.
                    </Typography>

                    {/* The answer to "who can see this?" up front, so it
                        doesn't have to be reconstructed by scrolling the
                        roster looking for ticks. Each chip removes that
                        person, which is faster than finding their row. */}
                    <Box
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'action.hover',
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Shared with ({selectedUsers.length})
                        </Typography>
                        {selectedUsers.length ? (
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                gap={0.75}
                                sx={{ mt: 0.75, maxHeight: 96, overflowY: 'auto' }}
                            >
                                {selectedUsers.map((user) => (
                                    <Chip
                                        key={String(user._id)}
                                        size="small"
                                        label={user.fullName}
                                        onDelete={() => toggleUser(String(user._id))}
                                        disabled={saving}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Not shared with anyone yet.
                            </Typography>
                        )}
                    </Box>

                    <TextField
                        placeholder="Search by name, email or role"
                        size="small"
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Chip size="small" label={`${selectedIds.size} selected`} sx={{ fontWeight: 600 }} />
                        <Button size="small" onClick={toggleAllFiltered} disabled={!filteredUsers.length}>
                            {allFilteredSelected ? 'Clear shown' : 'Select shown'}
                        </Button>
                    </Stack>

                    {loadError && <Alert severity="error">{loadError}</Alert>}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : filteredUsers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            {users.length ? 'No users match that search.' : 'No users available to share with.'}
                        </Typography>
                    ) : (
                        <List dense sx={{ maxHeight: 340, overflowY: 'auto' }}>
                            {filteredUsers.map((user) => {
                                const id = String(user._id);
                                return (
                                    <ListItemButton key={id} onClick={() => toggleUser(id)} sx={{ borderRadius: 2 }}>
                                        <Checkbox
                                            edge="start"
                                            checked={selectedIds.has(id)}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <Avatar
                                            sx={{ width: 28, height: 28, fontSize: 12, mr: 1.5, bgcolor: '#94a3b8' }}
                                        >
                                            {initials(user.fullName)}
                                        </Avatar>
                                        <ListItemText
                                            primary={user.fullName}
                                            secondary={[user.email, user.role].filter(Boolean).join(' · ')}
                                            primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                        {/* Marks saved access, unlike the
                                            checkbox, which also reflects
                                            edits not yet written. */}
                                        {initialSharedIds.has(id) && (
                                            <Chip
                                                size="small"
                                                label="Shared"
                                                variant="outlined"
                                                color="success"
                                                sx={{ ml: 1, height: 20, fontSize: 11, flexShrink: 0 }}
                                            />
                                        )}
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#ee4c3a' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || saving || Boolean(loadError)}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default ShareCampaignDialog;
